import type { FeatureCollection } from 'geojson';

import { api } from '@/services/api';

import type { GeoJsonGeometry } from '@shared/types/geojson';

import type { FacilitiesInPolygon, MapBatchParams, MapInput, MapObjectImage, MapObjectTag, MapSummary, MapTileConfig } from '../types';
import type { MapCalibration } from '../types';

interface ListResponse<T> {
  items: T[];
}

// API возвращает snake_case, но мы используем camelCase в типах
type MapTileConfigResponse = {
  id: number;
  name: string;
  url_pattern?: string | null; // Может быть null или пустой строкой
  max_zoom?: number | null;
  min_zoom?: number | null;
  coordinate_system?: string | null; // API возвращает snake_case, может быть null
  subdomains?: string[] | string | null;
  parent_map_tile_id?: number | null;
  is_tms?: boolean;
  copyright?: string | null;
  is_enable?: boolean;
  position?: number | null;
};

const buildBatchQuery = (params: MapBatchParams): string => {
  const search = new URLSearchParams();
  search.set('mapId', String(params.mapId));
  if (params.zoom !== undefined) {
    search.set('zoom', String(params.zoom));
  }
  if (params.bounds) {
    search.set('ne', params.bounds.ne.join(','));
    search.set('sw', params.bounds.sw.join(','));
  }
  params.layers?.forEach((layer) => search.append('layer', layer));
  // Фильтры по типам
  params.nodeTypeIDs?.forEach((id) => search.append('node_type_id', String(id)));
  params.cableLineTypeIDs?.forEach((id) => search.append('cable_line_type_id', String(id)));
  params.deviceTypeIDs?.forEach((id) => search.append('device_type_id', String(id)));
  return search.toString();
};

const normalizeTile = (tile: MapTileConfigResponse): MapTileConfig => {
  const { subdomains, coordinate_system, url_pattern, max_zoom, min_zoom, parent_map_tile_id, is_tms, is_enable, copyright, ...rest } = tile;
  
  // Нормализуем subdomains
  let normalizedSubdomains: string[] | undefined = undefined;
  if (subdomains) {
    if (Array.isArray(subdomains)) {
      normalizedSubdomains = subdomains.length > 0 ? subdomains : undefined;
    } else {
      const normalized = subdomains
        .split(',')
        .map((item: string) => item.trim())
        .filter((item): item is string => item.length > 0);
      normalizedSubdomains = normalized.length > 0 ? normalized : undefined;
    }
  }
  
  // Нормализуем url_pattern - может быть null, пустой строкой или отсутствовать
  // ВАЖНО: Если url_pattern === null или undefined, оставляем undefined (не пустую строку)
  // чтобы MapTileLayer мог использовать getTileUrlByName() для определения URL по имени
  const normalizedUrlPattern = url_pattern != null && url_pattern.trim().length > 0 
    ? url_pattern.trim() 
    : undefined;
  
  // Диагностика для разработки
  if (process.env.NODE_ENV === 'development') {
    if (!normalizedUrlPattern) {
      console.warn('[mapApi.normalizeTile] ⚠️ Tile without urlPattern:', {
        tileId: tile.id,
        tileName: tile.name,
        url_pattern: url_pattern,
        url_pattern_type: typeof url_pattern,
        note: 'This tile will use URL determined by name or fallback',
      });
    } else {
      console.log('[mapApi.normalizeTile] ✅ Tile with urlPattern:', {
        tileId: tile.id,
        tileName: tile.name,
        url_pattern_length: normalizedUrlPattern.length,
        url_pattern_preview: normalizedUrlPattern.substring(0, 80) + (normalizedUrlPattern.length > 80 ? '...' : ''),
      });
    }
  }
  
  // Маппим snake_case в camelCase
  return {
    ...rest,
    urlPattern: normalizedUrlPattern, // undefined, если url_pattern отсутствует (для использования getTileUrlByName)
    maxZoom: max_zoom ?? undefined,
    minZoom: min_zoom ?? undefined,
    coordinateType: coordinate_system || '', // ВАЖНО: маппим coordinate_system в coordinateType
    subdomains: normalizedSubdomains,
    parentId: parent_map_tile_id ?? undefined,
    isTms: is_tms ?? false,
    copyright: copyright || '',
    isEnable: is_enable,
  };
};

export interface MapIconURL {
  iconKey: string;
  url: string;
}

export interface MapIconURLsResponse {
  items: MapIconURL[];
}

/** Карточка дома: на проводе кадра у дома есть только id и цвет. */
export interface BuildingCard {
  id: number;
  number: string;
  address: string;
  apartCount: number;
  customerCount: number;
  isUnused: boolean;
  tariffGroupId?: number;
  tariffGroupName?: string;
  nodes: BuildingCardNode[];
}

export interface BuildingCardNode {
  id: number;
  name: string;
  nodeTypeName: string;
}

export const mapApi = {
  getBuildingCard: async (id: number | string): Promise<BuildingCard> => {
    const response = await api.get<BuildingCard>(`/map/buildings/${id}`);
    return response.data;
  },

  listMaps: async (): Promise<MapSummary[]> => {
    const response = await api.get<ListResponse<MapSummary>>('/map/maps');
    return response.data.items;
  },

  listCities: async (): Promise<MapSummary[]> => {
    const response = await api.get<ListResponse<MapSummary>>('/map/cities');
    return response.data.items;
  },

  createMap: async (data: MapInput): Promise<MapSummary> => {
    const response = await api.post<MapSummary>('/map/maps', data);
    return response.data;
  },

  updateMap: async (id: number, data: MapInput): Promise<MapSummary> => {
    const response = await api.put<MapSummary>(`/map/maps/${id}`, data);
    return response.data;
  },

  deleteMap: async (id: number): Promise<void> => {
    await api.delete(`/map/maps/${id}`);
  },

  listTiles: async (): Promise<MapTileConfig[]> => {
    const response = await api.get<ListResponse<MapTileConfigResponse>>('/map/tiles');
    const normalized = response.data.items.map(normalizeTile);
    
    // Диагностика для разработки
    if (process.env.NODE_ENV === 'development') {
      console.log('[mapApi.listTiles] Tiles loaded from API:', {
        totalTiles: normalized.length,
        tilesWithUrlPattern: normalized.filter(t => t.urlPattern && t.urlPattern.trim().length > 0).length,
        tilesWithoutUrlPattern: normalized.filter(t => !t.urlPattern || t.urlPattern.trim().length === 0).map(t => ({
          id: t.id,
          name: t.name,
          urlPattern: t.urlPattern,
        })),
        sampleTile: normalized[0] ? {
          id: normalized[0].id,
          name: normalized[0].name,
          urlPattern: normalized[0].urlPattern,
          hasUrlPattern: !!(normalized[0].urlPattern && normalized[0].urlPattern.trim().length > 0),
        } : null,
      });
    }
    
    return normalized;
  },

  getCalibration: async (mapId: number): Promise<MapCalibration> => {
    const response = await api.get<MapCalibration>(`/map/maps/${mapId}/calibration`);
    return response.data;
  },

  fetchBatch: async (params: MapBatchParams): Promise<FeatureCollection> => {
    const query = buildBatchQuery(params);
    const url = `/map/batch?${query}`;
    if (process.env.NODE_ENV === 'development') {
      console.log('[mapApi.fetchBatch] Request URL:', url);
      console.log('[mapApi.fetchBatch] Params:', {
        ...params,
        boundsDetails: params.bounds ? {
          ne: { lat: params.bounds.ne[0], lon: params.bounds.ne[1] },
          sw: { lat: params.bounds.sw[0], lon: params.bounds.sw[1] },
          latDiff: Math.abs(params.bounds.ne[0] - params.bounds.sw[0]),
          lngDiff: Math.abs(params.bounds.ne[1] - params.bounds.sw[1]),
        } : null,
      });
    }
    const response = await api.get<FeatureCollection>(url);
    if (process.env.NODE_ENV === 'development') {
      console.log('[mapApi.fetchBatch] Response:', {
        type: response.data?.type,
        featuresCount: response.data?.features?.length ?? 0,
        sampleFeature: response.data?.features?.[0],
        allFeatures: response.data?.features?.slice(0, 3), // Первые 3 фичи для отладки
      });
      
      // Если featuresCount = 0, логируем предупреждение с подробной информацией
      if ((response.data?.features?.length ?? 0) === 0) {
        console.warn('[mapApi.fetchBatch] ⚠️ API returned 0 features. Possible reasons:', {
          mapId: params.mapId,
          bounds: params.bounds,
          boundsDetails: params.bounds ? {
            ne: { lat: params.bounds.ne[0], lon: params.bounds.ne[1] },
            sw: { lat: params.bounds.sw[0], lon: params.bounds.sw[1] },
            latDiff: Math.abs(params.bounds.ne[0] - params.bounds.sw[0]),
            lngDiff: Math.abs(params.bounds.ne[1] - params.bounds.sw[1]),
          } : null,
          layers: params.layers,
          zoom: params.zoom,
          suggestions: [
            '1. Check if data exists in erp.map_objects for this mapId and object_type IN (label, image, polygon)',
            '2. Check if bounds intersect with data coordinates',
            '3. Try requesting other layers: node, house, customer',
            '4. Check backend logs for SQL query errors',
          ],
        });
      }
    }
    return response.data;
  },

  updateGeometry: async (objectId: string, geometry: GeoJsonGeometry): Promise<void> => {
    await api.put(`/map/objects/${objectId}/geometry`, {
      geometry,
    });
  },

  savePonFiberGeometry: async (params: {
    oltDeviceId: number;
    onuDeviceId: number;
    mapId: number;
    geometry: GeoJsonGeometry;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>('/map/pon/fiber/geometry', {
      oltDeviceId: params.oltDeviceId,
      onuDeviceId: params.onuDeviceId,
      mapId: params.mapId,
      geometry: params.geometry,
    });
    return response.data;
  },

  deleteObject: async (objectId: string): Promise<void> => {
    await api.delete(`/map/objects/${objectId}`);
  },

  createObject: async (payload: {
    objectType: 'label' | 'image' | 'polygon' | 'route';
    mapId: number;
    name?: string | null;
    properties?: Record<string, unknown>;
    geometry: GeoJsonGeometry;
  }): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/map/objects`, {
      objectType: payload.objectType,
      mapId: payload.mapId,
      name: payload.name,
      properties: payload.properties ?? {},
      geometry: payload.geometry,
    });
    return response.data;
  },

  // --- K6: метки-теги объекта карты и «что внутри полигона» ---

  listTagCatalog: async (): Promise<MapObjectTag[]> => {
    const response = await api.get<{ items: MapObjectTag[] }>('/map/tags');
    return response.data.items ?? [];
  },

  listObjectTags: async (objectId: string): Promise<MapObjectTag[]> => {
    const response = await api.get<{ items: MapObjectTag[] }>(`/map/objects/${objectId}/tags`);
    return response.data.items ?? [];
  },

  addObjectTag: async (objectId: string, tagId: number): Promise<void> => {
    await api.post(`/map/objects/${objectId}/tags`, { tagId });
  },

  removeObjectTag: async (objectId: string, tagId: number): Promise<void> => {
    await api.delete(`/map/objects/${objectId}/tags/${tagId}`);
  },

  facilitiesInside: async (objectId: string): Promise<FacilitiesInPolygon> => {
    const response = await api.get<FacilitiesInPolygon>(`/map/objects/${objectId}/facilities-inside`);
    return response.data;
  },

  // K7: картинка-подложка объекта карты. Отдельный путь записи, а не общая
  // ручка /attachments/upload: та ключуется целочисленным object_id, у объектов
  // карты ключ uuid.
  getObjectImage: async (objectId: string): Promise<MapObjectImage> => {
    const response = await api.get<MapObjectImage>(`/map/objects/${objectId}/image`);
    return response.data;
  },

  uploadObjectImage: async (objectId: string, file: File): Promise<MapObjectImage> => {
    const form = new FormData();
    form.append('file', file);
    // Content-Type обязателен явно: у инстанса api дефолт 'application/json',
    // и без переопределения axios отправляет форму без границы (boundary) —
    // сервер не может разобрать multipart и отвечает 400 invalid_request.
    // Тот же приём в рабочем аплоаде KML (cableLinesApi.importKml).
    const response = await api.post<MapObjectImage>(`/map/objects/${objectId}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteObjectImage: async (objectId: string): Promise<void> => {
    await api.delete(`/map/objects/${objectId}/image`);
  },

  listLayers: async (): Promise<AvailableLayers> => {
    const response = await api.get<AvailableLayers>('/map/layers');
    return response.data;
  },

  getIconURLs: async (iconKeys: string[]): Promise<MapIconURL[]> => {
    if (iconKeys.length === 0) {
      return [];
    }
    const keysParam = iconKeys.join(',');
    const response = await api.get<MapIconURLsResponse>(`/map/icons?keys=${encodeURIComponent(keysParam)}`);
    return response.data.items;
  },

  search: async (params: {
    query: string;
    searchType?: 'address' | 'objectId';
    objectType?: 'node' | 'building' | 'customer' | '';
  }): Promise<SearchResult> => {
    const response = await api.post<SearchResult>('/map/search', {
      query: params.query,
      search_type: params.searchType || 'objectId',
      object_type: params.objectType || '',
    });
    return response.data;
  },

  geocode: async (query: string, provider?: 'dadata' | 'yandex' | 'google' | 'nominatim'): Promise<GeocodeResult[]> => {
    const searchParams = new URLSearchParams();
    searchParams.set('query', query);
    if (provider) {
      searchParams.set('provider', provider);
    }
    const response = await api.get<ListResponse<GeocodeResult>>(`/map/search/geocode?${searchParams.toString()}`);
    return response.data.items;
  },

  reverseGeocode: async (lat: number, lon: number, provider?: 'nominatim' | 'dadata' | 'yandex' | 'google'): Promise<ReverseGeocodeResult> => {
    const searchParams = new URLSearchParams();
    searchParams.set('lat', lat.toString());
    searchParams.set('lon', lon.toString());
    if (provider) {
      searchParams.set('provider', provider);
    }
    const response = await api.get<ReverseGeocodeResult>(`/map/search/reverse-geocode?${searchParams.toString()}`);
    return response.data;
  },

  getSuggestions: async (
    query: string,
    provider?: 'dadata' | 'yandex' | 'google' | 'nominatim',
    bounds?: { ne: [number, number]; sw: [number, number] }
  ): Promise<SuggestionsResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('query', query);
    if (provider) {
      searchParams.set('provider', provider);
    }
    if (bounds) {
      searchParams.set('ne', `${bounds.ne[0]},${bounds.ne[1]}`);
      searchParams.set('sw', `${bounds.sw[0]},${bounds.sw[1]}`);
    }
    const response = await api.get<SuggestionsResponse>(`/map/search/suggestions?${searchParams.toString()}`);
    return {
      items: response.data.items ?? [],
      geocoderWarning: response.data.geocoderWarning,
      geocoderProvider: response.data.geocoderProvider,
    };
  },

  fetchPonMapData: async (params: {
    mapId: number;
    bounds?: {
      ne: [number, number];
      sw: [number, number];
    };
  }): Promise<PonMapData> => {
    const searchParams = new URLSearchParams();
    searchParams.set('mapId', String(params.mapId));
    if (params.bounds) {
      searchParams.set('ne', params.bounds.ne.join(','));
      searchParams.set('sw', params.bounds.sw.join(','));
    }
    const response = await api.get<PonMapData>(`/map/pon?${searchParams.toString()}`);
    return response.data;
  },

  fetchGpsRoutes: async (params: {
    mapId: number;
    bounds?: {
      ne: [number, number];
      sw: [number, number];
    };
    date?: string; // YYYY-MM-DD
    employeeId?: number;
    transportId?: number;
  }): Promise<GpsRoutesData> => {
    const searchParams = new URLSearchParams();
    searchParams.set('mapId', String(params.mapId));
    if (params.bounds) {
      searchParams.set('ne', params.bounds.ne.join(','));
      searchParams.set('sw', params.bounds.sw.join(','));
    }
    if (params.date) {
      searchParams.set('date', params.date);
    }
    if (params.employeeId) {
      searchParams.set('employee_id', String(params.employeeId));
    }
    if (params.transportId) {
      searchParams.set('transport_id', String(params.transportId));
    }
    const response = await api.get<GpsRoutesData>(`/map/gps?${searchParams.toString()}`);
    return response.data;
  },

  fetchTasks: async (params: {
    mapId: number;
    bounds?: {
      ne: [number, number];
      sw: [number, number];
    };
    taskTypeIds?: number[];
    statusIds?: number[];
    employeeIds?: number[];
  }): Promise<TasksData> => {
    const searchParams = new URLSearchParams();
    searchParams.set('mapId', String(params.mapId));
    if (params.bounds) {
      searchParams.set('ne', params.bounds.ne.join(','));
      searchParams.set('sw', params.bounds.sw.join(','));
    }
    if (params.taskTypeIds) {
      params.taskTypeIds.forEach((id) => {
        searchParams.append('task_type_id', String(id));
      });
    }
    if (params.statusIds) {
      params.statusIds.forEach((id) => {
        searchParams.append('status_id', String(id));
      });
    }
    if (params.employeeIds) {
      params.employeeIds.forEach((id) => {
        searchParams.append('employee_id', String(id));
      });
    }
    const response = await api.get<TasksData>(`/map/tasks?${searchParams.toString()}`);
    return response.data;
  },

  fetchCoverageCircles: async (params: {
    mapId: number;
    bounds?: {
      ne: [number, number];
      sw: [number, number];
    };
  }): Promise<CoverageCirclesData> => {
    const searchParams = new URLSearchParams();
    searchParams.set('mapId', String(params.mapId));
    if (params.bounds) {
      searchParams.set('ne', params.bounds.ne.join(','));
      searchParams.set('sw', params.bounds.sw.join(','));
    }
    const response = await api.get<CoverageCirclesData>(`/map/coverage-circles?${searchParams.toString()}`);
    return response.data;
  },

  exportKML: async (objectId: string): Promise<Blob> => {
    const response = await api.get(`/map/objects/${objectId}/kml`, {
      responseType: 'blob',
    });
    return response.data;
  },

  importKML: async (objectId: string, kmlData: string): Promise<void> => {
    await api.post(`/map/objects/${objectId}/kml`, {
      data: kmlData,
    });
  },

  measureDistance: async (coordinates: [number, number][]): Promise<{ distance: number; distanceKm: number }> => {
    const response = await api.post<{ distance: number; distanceKm: number }>('/map/measure', {
      coordinates,
    });
    return response.data;
  },

  buildBuffer: async (geometry: string, radiusMeters: number): Promise<{ geometry: any }> => {
    const response = await api.post<{ geometry: any }>('/map/buffer', {
      geometry,
      radiusMeters,
    });
    return response.data;
  },

  findRoute: async (params: {
    startNodeId: number;
    endNodeId: number;
    mapId?: number;
  }): Promise<RouteResult> => {
    const response = await api.post<RouteResult>('/map/route', {
      startNodeId: params.startNodeId,
      endNodeId: params.endNodeId,
      mapId: params.mapId,
    });
    return response.data;
  },
};

export interface NodeWritePayload {
  type_id?: number;
  number?: string | null;
  location?: string | null;
  comment?: string | null;
  lat?: number;
  lon?: number;
  is_planned?: boolean;
}

export interface NodeWriteResponse {
  result: 'OK';
  id?: number;
  data?: {
    id: number;
    name?: string;
    node_type_id?: number | null;
    object_number?: string | null;
    location?: string | null;
    comment?: string | null;
  };
}

export const nodeApi = {
  create: async (payload: NodeWritePayload): Promise<NodeWriteResponse> => {
    const response = await api.post<NodeWriteResponse>('/nodes', payload);
    return response.data;
  },

  update: async (id: number, payload: NodeWritePayload): Promise<NodeWriteResponse> => {
    const response = await api.put<NodeWriteResponse>(`/nodes/${id}`, payload);
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(`/nodes/${id}`);
  },

  changeCustomIcon: async (id: number, customIconId: number): Promise<NodeWriteResponse> => {
    const response = await api.put<NodeWriteResponse>(`/nodes/${id}/custom-icon`, {
      custom_icon_id: customIconId,
    });
    return response.data;
  },

  addMark: async (id: number, markId: number): Promise<void> => {
    await api.post(`/nodes/${id}/marks`, { mark_id: markId });
  },

  deleteMark: async (id: number, markId: number): Promise<void> => {
    await api.delete(`/nodes/${id}/marks/${markId}`);
  },
};

export interface RouteResult {
  path: RouteSegment[];
  totalLength: number;
  nodes: RouteNode[];
}

export interface RouteSegment {
  cableLineId: number;
  name: string;
  geometry: string; // GeoJSON LineString
  length?: number;
  fromNodeId: number;
  toNodeId: number;
}

export interface RouteNode {
  nodeId: number;
  name: string;
  coordinates: [number, number]; // [lat, lon]
}

export interface SearchResult {
  objectType: 'node' | 'building' | 'customer';
  objectId: number;
  name: string;
  coordinates: [number, number]; // [lat, lon]
}

export interface GeocodeResult {
  address: string;
  coordinates: [number, number]; // [lat, lon]
  provider: 'dadata' | 'yandex' | 'google' | 'nominatim';
}

export interface ReverseGeocodeResult {
  address: string;
  state: string;
  city: string;
  town: string;
  village: string;
  provider: string;
  /** Провайдер не ответил. Сервер называет это полем, а не пятисоткой. */
  providerUnavailable?: boolean;
}

export interface AddressSuggestion {
  value: string;
  address: string;
  coordinates: [number, number];
  provider: string;
  /** 'local' — объект своей базы, 'geocoder' — находка внешнего геокодера. */
  source: 'local' | 'geocoder';
  objectType?: 'node' | 'building' | 'customer';
  objectId?: number;
  subtitle?: string;
}

/**
 * Ответ подсказок поиска. Провал внешнего геокодера приезжает полем, а не
 * ошибкой запроса: подсказки своей базы от него не зависят.
 */
export interface SuggestionsResponse {
  items: AddressSuggestion[];
  geocoderWarning?: 'provider_unavailable' | 'provider_failed';
  geocoderProvider?: string;
}

export interface PonMapData {
  devices: Array<{
    id: number;
    device_id: number;
    type: 'olt' | 'onu';
    name: string;
    coordinates: [number, number]; // [lat, lon]
    status?: 'online' | 'offline' | 'unknown';
    level?: number;
    port?: string;
  }>;
  fibers: Array<{
    id: string;
    olt_device_id: number;
    onu_device_id: number;
    olt_coordinates: [number, number];
    onu_coordinates: [number, number];
    port?: string;
    level?: number;
  }>;
}

export interface GpsRoutesData {
  trackers: Array<{
    id: number;
    type: 'employee' | 'transport';
    name: string;
    coordinates: [number, number]; // [lat, lon]
    speed?: number;
    dateAdd: number; // Unix timestamp
  }>;
  routes: Array<{
    id: string;
    trackerId: number;
    trackerType: 'employee' | 'transport';
    coordinates: [number, number][]; // Массив точек [lat, lon]
    speed?: number;
    color: string;
    dateStart: number;
    dateEnd: number;
  }>;
}

export interface MapTask {
  id: number;
  taskTypeId: number;
  taskTypeName: string;
  statusId: number;
  statusName: string;
  coordinates: [number, number]; // [lat, lon]
  commentShort?: string | null;
  isHighPriority: boolean;
  dateWork?: number | null;
  dateWorkDue?: number | null;
  nodeId?: number | null;
  buildingId?: number | null;
  addressId?: number | null;
  customerId?: number | null;
}

export interface TasksData {
  tasks: MapTask[];
}

export interface CoverageCircle {
  buildingId: number;
  coordinates: [number, number]; // [lat, lon]
  customerCount: number;
  radius: number; // Радиус в метрах (10-200)
  buildingName?: string | null;
}

export interface CoverageCirclesData {
  circles: CoverageCircle[];
}

export interface AvailableLayers {
  nodeTypes: LayerNodeType[];
  cableLineTypes: LayerCableLineType[];
  deviceTypes: LayerDeviceType[];
}

export interface LayerNodeType {
  id: number;
  name: string;
  singleName: string;
  position?: number;
  layerKey: string; // "node_<id>"
}

export interface LayerCableLineType {
  id: number;
  name: string;
  layerKey: string; // "cable_<id>"
}

export interface LayerDeviceType {
  id: number;
  name: string;
  typeId: number;
  layerKey: string; // "device_<id>"
}
